/**
 * `/sign-up?code=…` — finalize account creation behind an invite.
 *
 * Validates the code on mount (so we can prefill the intended email if the
 * invite was bound to a specific address) and on submit. Successful signup
 * also triggers a magic-link email so the user can sign in immediately.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthShell } from '../components/layout/AuthShell';
import { redeemInvite, signUp } from '../api/invites';
import { ApiCallError } from '../api/client';

export function SignUpPage() {
  const [params] = useSearchParams();
  const code = params.get('code') ?? '';
  const navigate = useNavigate();

  const [intendedEmail, setIntendedEmail] = useState<string | null>(null);
  const [validating, setValidating] = useState(true);
  const [validationError, setValidationError] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!code) {
      setValidating(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = await redeemInvite({ code });
        if (cancelled) return;
        if (!result.valid) {
          setValidationError(result.reason ?? 'This invite is no longer valid.');
        } else {
          setIntendedEmail(result.intendedEmail);
        }
      } catch (err) {
        if (cancelled) return;
        setValidationError(
          err instanceof ApiCallError ? err.apiError.message : 'Could not verify invite.'
        );
      } finally {
        if (!cancelled) setValidating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code]);

  if (!code) return <Navigate to="/invite" replace />;

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setSubmitError(null);
    setSubmitting(true);
    const form = new FormData(e.currentTarget);
    try {
      await signUp({
        code,
        email: String(form.get('email') ?? ''),
        displayName: String(form.get('displayName') ?? ''),
        handle: String(form.get('handle') ?? ''),
      });
      navigate('/sign-in/sent', {
        state: { email: String(form.get('email') ?? '') },
      });
    } catch (err) {
      setSubmitError(
        err instanceof ApiCallError ? err.apiError.message : 'Could not create account.'
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell topRight={<Link to="/sign-in">Sign in</Link>}>
      <div className="auth-card">
        <div className="auth-eyebrow">
          <span className="num">04</span> · Create account
        </div>
        <h1 className="auth-title">
          Create your <em>account.</em>
        </h1>
        <p className="auth-sub">A handle is how teammates will find you on Eloquor.</p>

        {validating ? (
          <div className="helper">
            <span className="dot" /> Checking invite…
          </div>
        ) : validationError ? (
          <div className="banner danger">{validationError}</div>
        ) : (
          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="field">
              <label className="label" htmlFor="email">
                Email
              </label>
              <input
                className="input"
                id="email"
                name="email"
                type="email"
                required
                defaultValue={intendedEmail ?? ''}
                readOnly={Boolean(intendedEmail)}
                placeholder="you@school.edu"
              />
            </div>
            <div className="field">
              <label className="label" htmlFor="displayName">
                Full name
              </label>
              <input
                className="input"
                id="displayName"
                name="displayName"
                type="text"
                placeholder="Raymond Xu"
                required
              />
            </div>
            <div className="field">
              <label className="label" htmlFor="handle">
                Handle
              </label>
              <input
                className="input"
                id="handle"
                name="handle"
                type="text"
                pattern="[a-zA-Z0-9_-]+"
                placeholder="xu_r"
                minLength={2}
                maxLength={32}
                required
              />
            </div>

            {submitError && (
              <div className="helper err">
                <span className="dot" />
                <span>{submitError}</span>
              </div>
            )}

            <button className="btn-primary" type="submit" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create account'} <span aria-hidden>→</span>
            </button>
          </form>
        )}
      </div>
    </AuthShell>
  );
}
